import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/useToast';
import { partnerApi, type Partner, type PartnerSigningKey } from '../../lib/api';
import { SETTINGS_STRINGS } from '../../lib/constants';
import { ConfirmDialog } from './team/ConfirmDialog';
import styles from './ApiTab.module.css';

// Utility function to base64url encode a string
const base64UrlEncode = (str: string): string => {
  const base64 = btoa(str);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

// Utility function to convert a string to Uint8Array
const stringToUint8Array = (str: string) => {
  const encoder = new TextEncoder();
  return encoder.encode(str);
};

// Async function to generate a JWT token using Web Crypto API
const generateJWT = async (payload: object, secret: string): Promise<string> => {
  try {
    const header = {
      alg: 'HS256',
      typ: 'JWT',
    };

    const encodedHeader = base64UrlEncode(JSON.stringify(header));
    const encodedPayload = base64UrlEncode(JSON.stringify(payload));
    const dataToSign = `${encodedHeader}.${encodedPayload}`;

    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );

    const dataBuffer = stringToUint8Array(dataToSign);
    const signature = await window.crypto.subtle.sign('HMAC', cryptoKey, dataBuffer);

    const signatureArray = Array.from(new Uint8Array(signature));
    const signatureString = String.fromCharCode(...signatureArray);
    const encodedSignature = base64UrlEncode(signatureString);

    return `${encodedHeader}.${encodedPayload}.${encodedSignature}`;
  } catch (error) {
    console.error('Error generating JWT token:', error);
    throw error;
  }
};

/**
 * API Configuration Tab Component
 * Allows partners to view JWT secret and generate API tokens
 */
export const ApiTab: React.FC = () => {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [showJwtSecret, setShowJwtSecret] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);
  const [apiToken, setApiToken] = useState<string>('');
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [latestRawSecret, setLatestRawSecret] = useState<string | null>(null);
  const [latestRawSecretKeyId, setLatestRawSecretKeyId] = useState<string | null>(null);
  const [pendingDeactivationKey, setPendingDeactivationKey] = useState<PartnerSigningKey | null>(null);

  const { data: partner, isLoading } = useQuery<Partner>({
    queryKey: ['partner'],
    queryFn: () => partnerApi.getPartner(),
  });
  const effectiveJwtSecret = latestRawSecret ?? partner?.jwtSecret ?? null;

  const { data: signingKeys = [], isLoading: isLoadingKeys } = useQuery<PartnerSigningKey[]>({
    queryKey: ['partner-signing-keys'],
    queryFn: () => partnerApi.getPartnerSigningKeys(),
  });

  const createKeyMutation = useMutation({
    mutationFn: () => partnerApi.createPartnerSigningKey(),
    onSuccess: async result => {
      setLatestRawSecret(result.rawSecret);
      setLatestRawSecretKeyId(result.id);
      setApiToken('');
      setTokenError(null);
      await queryClient.invalidateQueries({ queryKey: ['partner'] });
      await queryClient.invalidateQueries({ queryKey: ['partner-signing-keys'] });
      addToast({
        variant: 'success',
        title: 'New signing key created',
        description: 'Store the secret now. The dashboard will not show it again.',
      });
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to create signing key',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const deactivateKeyMutation = useMutation({
    mutationFn: (keyId: string) => partnerApi.deactivatePartnerSigningKey(keyId),
    onSuccess: async deactivatedKey => {
      if (latestRawSecretKeyId === deactivatedKey.id) {
        setLatestRawSecret(null);
        setLatestRawSecretKeyId(null);
      }
      setApiToken('');
      setTokenError(null);
      await queryClient.invalidateQueries({ queryKey: ['partner'] });
      await queryClient.invalidateQueries({ queryKey: ['partner-signing-keys'] });
      addToast({
        variant: 'success',
        title: 'Signing key deactivated',
        description: 'Reach no longer accepts JWTs signed with that key.',
      });
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to deactivate signing key',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
    onSettled: () => {
      setPendingDeactivationKey(null);
    },
  });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
    addToast({
      variant: 'success',
      title: SETTINGS_STRINGS.API_CONFIGURATION.COPY_SUCCESS,
    });
  };

  const generateApiToken = async () => {
    if (!effectiveJwtSecret) {
      setTokenError('Partner does not have a JWT secret configured');
      return;
    }
    if (!partner) {
      setTokenError('Partner information is still loading');
      return;
    }

    setIsGeneratingToken(true);
    setTokenError(null);

    try {
      const payload = {
        platformId: partner.id,
        type: 'platform',
      };

      const token = await generateJWT(payload, effectiveJwtSecret);
      setApiToken(token);
    } catch (error) {
      console.error('Error generating API token:', error);
      setTokenError(
        'Failed to generate API token: ' + (error instanceof Error ? error.message : String(error)),
      );
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const obfuscateText = (text: string, showFull: boolean): string => {
    if (!text) return 'Not available';
    if (showFull) return text;
    return '••••••••••••••••';
  };

  const formatTimestamp = (value: string | null) => {
    if (!value) return 'Never';
    return new Date(value).toLocaleString();
  };

  const activeKeys = signingKeys.filter(key => key.status === 'active');
  const deactivatedKeys = signingKeys.filter(key => key.status === 'deactivated');

  const handleDeactivateKey = (key: PartnerSigningKey) => {
    setPendingDeactivationKey(key);
  };

  if (isLoading || isLoadingKeys) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* API Credentials */}
      <Card>
        <CardContent className="pt-6 space-y-6">
          {/* Partner ID */}
          <div className="space-y-2">
            <Label htmlFor="partnerId">Partner ID</Label>
            <div className="flex gap-2">
              <Input
                id="partnerId"
                type="text"
                value={partner?.id || ''}
                readOnly
                className="font-mono flex-1"
              />
              <button
                type="button"
                onClick={() => partner?.id && handleCopy(partner.id, 'id')}
                className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                {copied === 'id' ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {/* Partner Hash */}
          <div className="space-y-2">
            <Label htmlFor="partnerHash">Partner Hash</Label>
            <div className="flex gap-2">
              <Input
                id="partnerHash"
                type="text"
                value={partner?.hash || ''}
                readOnly
                className="font-mono flex-1"
              />
              <button
                type="button"
                onClick={() => partner?.hash && handleCopy(partner.hash, 'hash')}
                className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                {copied === 'hash' ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {/* JWT Secret */}
          <div className="space-y-2">
            <Label htmlFor="jwtSecret">JWT Secret</Label>
            <div className="flex gap-2">
              <Input
                id="jwtSecret"
                type={showJwtSecret ? 'text' : 'password'}
                value={
                  effectiveJwtSecret
                    ? showJwtSecret
                      ? effectiveJwtSecret
                      : obfuscateText(effectiveJwtSecret, false)
                    : 'Not available'
                }
                readOnly
                className="font-mono flex-1"
              />
              <button
                type="button"
                onClick={() => setShowJwtSecret(!showJwtSecret)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                {showJwtSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => effectiveJwtSecret && handleCopy(effectiveJwtSecret, 'jwt')}
                className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                {copied === 'jwt' ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
            {!effectiveJwtSecret && (
              <p className="text-xs text-muted-foreground">
                No JWT secret available. Contact support to configure authentication.
              </p>
            )}
          </div>

          {/* API Token */}
          <div className="space-y-2">
            <Label htmlFor="apiToken">API Token</Label>
            {apiToken ? (
              <div className="flex gap-2">
                <Input
                  id="apiToken"
                  type={showApiToken ? 'text' : 'password'}
                  value={showApiToken ? apiToken : obfuscateText(apiToken, false)}
                  readOnly
                  className="font-mono flex-1"
                />
                <button
                  type="button"
                  onClick={() => setShowApiToken(!showApiToken)}
                  className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                  {showApiToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => handleCopy(apiToken, 'token')}
                  className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                  {copied === 'token' ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            ) : (
              <div className="border rounded-md p-4 bg-muted/50">
                <p className="text-sm text-muted-foreground mb-2">
                  Generate a JWT token with these claims:
                </p>
                <pre className="bg-background p-3 rounded text-xs mb-3 overflow-x-auto">
                  {`{
  "platformId": "${partner?.id}",
  "type": "platform"
}`}
                </pre>
                <button
                  onClick={generateApiToken}
                  disabled={isGeneratingToken || !effectiveJwtSecret}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
                  {isGeneratingToken ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    'Generate API Token'
                  )}
                </button>
                {tokenError && <p className="mt-2 text-xs text-destructive">{tokenError}</p>}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className={styles.signingKeysCardContent}>
          <div className={styles.signingKeysHeader}>
            <div className={styles.signingKeysHeaderText}>
              <h3 className={styles.signingKeysHeading}>Signing Keys</h3>
              <p className={styles.signingKeysDescription}>
                Reach accepts JWTs signed with any active key. Create and deactivate keys explicitly as
                your downstream systems change over.
              </p>
            </div>
            <Button
              onClick={() => createKeyMutation.mutate()}
              disabled={createKeyMutation.isPending}
              className={styles.createKeyButton}>
              {createKeyMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create New Key'
              )}
            </Button>
          </div>

          {latestRawSecret && (
            <div className={styles.latestSecretNotice}>
              <p className={styles.latestSecretTitle}>New signing secret</p>
              <p className={styles.latestSecretDescription}>
                Copy this secret and store it in your secret manager. You can view it again from this
                page, but you won't see it from anywhere else.
              </p>
              <div className={styles.latestSecretActions}>
                <Input value={latestRawSecret} readOnly className={styles.latestSecretInput} />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleCopy(latestRawSecret, 'latest-raw-secret')}>
                  {copied === 'latest-raw-secret' ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
          )}

          <div className={styles.signingKeyGroups}>
            {activeKeys.length > 0 && (
              <div className={styles.signingKeySection}>
                <div>
                  <h4 className={styles.signingKeySectionHeading}>Active Keys</h4>
                  <p className={styles.signingKeySectionDescription}>
                    Reach accepts JWTs signed with any active key listed here.
                  </p>
                </div>
                {activeKeys.map(key => (
                  <div key={key.id} className={styles.signingKeyCard}>
                    <div className={styles.signingKeyCardHeader}>
                      <div>
                        <div className={styles.signingKeyStatusRow}>
                          <span className={styles.signingKeyStatusBadge} data-status="active">
                            Active
                          </span>
                          <span className={styles.signingKeyId}>{key.id}</span>
                        </div>
                        <div className={styles.signingKeyMeta}>
                          <p>Created: {formatTimestamp(key.createdAt)}</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleDeactivateKey(key)}
                        disabled={
                          deactivateKeyMutation.isPending && deactivateKeyMutation.variables === key.id
                        }>
                        {deactivateKeyMutation.isPending && deactivateKeyMutation.variables === key.id
                          ? 'Updating...'
                          : 'Deactivate Key'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {deactivatedKeys.length > 0 && (
              <div className={styles.signingKeySection}>
                <div>
                  <h4 className={styles.signingKeySectionHeading}>Deactivated Keys</h4>
                  <p className={styles.signingKeySectionDescription}>
                    These keys are retained as audit history only and are no longer accepted by Reach.
                  </p>
                </div>
                {deactivatedKeys.map(key => (
                  <div key={key.id} className={styles.signingKeyCard}>
                    <div className={styles.signingKeyStatusRow}>
                      <span className={styles.signingKeyStatusBadge} data-status="deactivated">
                        Deactivated
                      </span>
                      <span className={styles.signingKeyId}>{key.id}</span>
                    </div>
                    <div className={styles.signingKeyMeta}>
                      <p>Created: {formatTimestamp(key.createdAt)}</p>
                      <p>Deactivated: {formatTimestamp(key.deactivatedAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-base font-semibold text-foreground mb-4">About API Authentication</h3>
          <div className="text-sm text-muted-foreground space-y-3">
            <div>
              <p className="font-medium mb-2">How it works:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>API tokens are generated using JWT (JSON Web Token) standard</li>
                <li>The token contains your partner ID and is signed with your JWT secret</li>
                <li>Tokens can be used to authenticate API requests to Reach</li>
                <li>Keep your JWT secret secure and never share it publicly</li>
                <li>You can keep multiple active signing keys during cutovers</li>
                <li>Deactivating a key immediately stops Reach from accepting JWTs signed with it</li>
                <li>You can generate new tokens as needed without affecting existing ones</li>
              </ul>
            </div>
            <div className="pt-2 border-t">
              <p className="font-medium mb-1">Learn more:</p>
              <a
                href="https://docs.embedreach.com/samples/jwt-tokens"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1">
                View JWT implementation guide and examples
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingDeactivationKey !== null}
        onClose={() => setPendingDeactivationKey(null)}
        onConfirm={() => {
          if (pendingDeactivationKey) {
            deactivateKeyMutation.mutate(pendingDeactivationKey.id);
          }
        }}
        isPending={deactivateKeyMutation.isPending}
        title="Deactivate signing key?"
        description="JWTs signed with this key will stop working immediately after deactivation."
        confirmLabel="Deactivate Now"
      />
    </div>
  );
};
